import React, { useEffect, useState, useRef } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import api from '../api';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL}${url}`;
};

const slugify = (str) =>
  str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const EMPTY_FORM = {
  title: '',
  slug: '',
  product_limit: 4,
  is_active: true,
  show_in_banner: false,
  product_ids: [],
};

const ProductCheckbox = ({ product, selected, onToggle }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: '.75rem',
    padding: '.5rem .75rem', borderRadius: 6, cursor: 'pointer',
    background: selected ? '#f0ede8' : 'transparent',
    transition: 'background .1s',
  }}>
    <input type="checkbox" checked={selected} onChange={() => onToggle(product.id)} />
    {(product.image_url || product.images?.[0]?.image_url) ? (
      <img src={getImageUrl(product.image_url || product.images[0].image_url)} alt={product.name}
        style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
    ) : (
      <div style={{ width: 36, height: 36, background: '#e5e7eb', borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem', flexShrink: 0 }}>📦</div>
    )}
    <span style={{ flex: 1, fontSize: '.9rem' }}>{product.name}</span>
    <span style={{ color: '#888', fontSize: '.8rem', whiteSpace: 'nowrap' }}>${parseFloat(product.price).toFixed(2)}</span>
  </label>
);

const AdminSections = () => {
  const [sections, setSections]       = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [alert, setAlert]             = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [editingSection, setEditing]  = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [productSearch, setPSearch]   = useState('');
  const [dragOver, setDragOver]       = useState(null);
  const [homeLayout, setHomeLayout]   = useState('horizontal');
  const [layoutSaving, setLayoutSaving] = useState(false);
  const dragItem = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [sectRes, prodRes, settRes] = await Promise.allSettled([
      api.get('/api/admin/sections'),
      api.get('/api/products?admin=true'),
      api.get('/api/settings'),
    ]);
    if (sectRes.status === 'fulfilled') setSections(sectRes.value.data.data);
    else showAlert('error', '⚠️ Erreur chargement sections.');
    if (prodRes.status === 'fulfilled') setAllProducts(prodRes.value.data.data);
    if (settRes.status === 'fulfilled') setHomeLayout(settRes.value.data.data.home_layout || 'horizontal');
    setLoading(false);
  };

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  const toggleHomeLayout = async () => {
    const newLayout = homeLayout === 'horizontal' ? 'grid' : 'horizontal';
    setLayoutSaving(true);
    try {
      await api.put('/api/settings', { home_layout: newLayout });
      setHomeLayout(newLayout);
      showAlert('success', `Mode d'affichage : ${newLayout === 'grid' ? 'Grille de catégories' : 'Scroll horizontal'} ✅`);
    } catch {
      showAlert('error', 'Erreur lors du changement de mode.');
    } finally {
      setLayoutSaving(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setPSearch(''); setShowModal(true); };
  const openEdit = (section) => {
    setEditing(section);
    setForm({
      title: section.title, slug: section.slug,
      product_limit: section.product_limit, is_active: !!section.is_active,
      show_in_banner: !!section.show_in_banner,
      product_ids: (section.products || []).map(p => p.id),
    });
    setPSearch(''); setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setForm(EMPTY_FORM); };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setForm(f => ({ ...f, title: newTitle, slug: f.slug === slugify(f.title) || !f.slug ? slugify(newTitle) : f.slug }));
  };

  const toggleProduct = (productId) => {
    setForm(f => ({
      ...f,
      product_ids: f.product_ids.includes(productId)
        ? f.product_ids.filter(id => id !== productId)
        : [...f.product_ids, productId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showAlert('error', 'Le titre est requis.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title), product_limit: parseInt(form.product_limit) || 4 };
      if (editingSection) {
        await api.put(`/api/admin/sections/${editingSection.id}`, payload);
        showAlert('success', 'Section mise à jour ✅');
      } else {
        await api.post('/api/admin/sections', payload);
        showAlert('success', 'Section créée ✅');
      }
      closeModal(); await loadData();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette section définitivement ?')) return;
    try { await api.delete(`/api/admin/sections/${id}`); showAlert('success', 'Section supprimée.'); await loadData(); }
    catch { showAlert('error', 'Erreur lors de la suppression.'); }
  };

  const handleToggleActive = async (section) => {
    try { await api.put(`/api/admin/sections/${section.id}`, { is_active: !section.is_active }); await loadData(); }
    catch { showAlert('error', 'Erreur.'); }
  };

  const handleToggleBanner = async (section) => {
    try { await api.put(`/api/admin/sections/${section.id}`, { show_in_banner: !section.show_in_banner }); await loadData(); }
    catch { showAlert('error', 'Erreur.'); }
  };

  const handleDragStart = (index) => { dragItem.current = index; };
  const handleDragEnter = (index) => { setDragOver(index); };
  const handleDragEnd = async () => {
    const from = dragItem.current; const to = dragOver;
    dragItem.current = null; setDragOver(null);
    if (from === null || to === null || from === to) return;
    const reordered = [...sections];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const withOrder = reordered.map((s, i) => ({ ...s, display_order: i + 1 }));
    setSections(withOrder);
    try {
      await api.patch('/api/admin/sections/reorder', { orders: withOrder.map(s => ({ id: s.id, display_order: s.display_order })) });
    } catch { showAlert('error', "Erreur lors de la réorganisation."); await loadData(); }
  };

  const filteredProducts = allProducts.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()));

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-main">

        <div className="admin-header">
          <div>
            <h1>Sections Home Page</h1>
            <p>Gérez les sections de produits affichées sur la page d'accueil.</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>

            {/* Toggle mode d'affichage */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '.75rem',
              background: '#f8f7f4', borderRadius: 10, padding: '.5rem 1rem',
              border: '1px solid #e5e7eb',
            }}>
              <span style={{ fontSize: '.85rem', fontWeight: 600, color: '#666' }}>Mode :</span>
              <button
                onClick={() => { if (homeLayout !== 'horizontal' && !layoutSaving) toggleHomeLayout(); }}
                style={{
                  padding: '.35rem .8rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: '.82rem', transition: 'all .15s',
                  background: homeLayout === 'horizontal' ? '#1a1a1a' : 'white',
                  color: homeLayout === 'horizontal' ? 'white' : '#888',
                  boxShadow: homeLayout === 'horizontal' ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                }}
              >↔ Scroll</button>
              <button
                onClick={() => { if (homeLayout !== 'grid' && !layoutSaving) toggleHomeLayout(); }}
                style={{
                  padding: '.35rem .8rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: '.82rem', transition: 'all .15s',
                  background: homeLayout === 'grid' ? '#1a1a1a' : 'white',
                  color: homeLayout === 'grid' ? 'white' : '#888',
                  boxShadow: homeLayout === 'grid' ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                }}
              >⊞ Grille</button>
            </div>

            <button className="btn btn-primary" onClick={openCreate}>+ Ajouter une section</button>
          </div>
        </div>

        {alert && (
          <div className={`alert alert-${alert.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
            {alert.msg}
            <button onClick={() => setAlert(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Chargement...</div>
        ) : sections.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <p style={{ marginBottom: '1.5rem' }}>Aucune section. Créez votre première section.</p>
            <button className="btn btn-primary" onClick={openCreate}>+ Créer ma première section</button>
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0ede8', background: '#faf9f7' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', width: 50, color: '#888', fontWeight: 500 }}>Ordre</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 500 }}>Titre</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 500 }}>Slug</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 500 }}>Produits</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 500 }}>Statut</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 500 }}>Banner</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 500 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section, index) => (
                  <tr key={section.id} draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => e.preventDefault()}
                    style={{ borderBottom: '1px solid #f0ede8', background: dragOver === index ? '#f0ede8' : 'white', transition: 'background .15s', cursor: 'grab' }}
                  >
                    <td style={{ padding: '1rem', color: '#aaa', userSelect: 'none', fontSize: '1.1rem' }}>⠿</td>
                    <td style={{ padding: '1rem', fontWeight: 600 }}>{section.title}</td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', color: '#888', fontSize: '.85rem' }}>{section.slug}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ background: '#f0ede8', borderRadius: 12, padding: '.2rem .75rem', fontSize: '.85rem', fontWeight: 500 }}>
                        {(section.products || []).length} / {section.product_limit}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button onClick={() => handleToggleActive(section)} style={{
                        background: section.is_active ? '#22c55e' : '#d1d5db', color: 'white',
                        border: 'none', borderRadius: 12, padding: '.25rem .9rem', fontSize: '.8rem', cursor: 'pointer', fontWeight: 600,
                      }}>{section.is_active ? 'Actif' : 'Inactif'}</button>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button onClick={() => handleToggleBanner(section)} style={{
                        background: section.show_in_banner ? '#3b82f6' : '#d1d5db', color: 'white',
                        border: 'none', borderRadius: 12, padding: '.25rem .9rem', fontSize: '.8rem', cursor: 'pointer', fontWeight: 600,
                      }}>{section.show_in_banner ? '🔗 Visible' : 'Masqué'}</button>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline" style={{ marginRight: '.5rem', padding: '.4rem .9rem', fontSize: '.85rem' }} onClick={() => openEdit(section)}>✏️ Modifier</button>
                      <button onClick={() => handleDelete(section.id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '.4rem .9rem', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600 }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ padding: '.75rem 1rem', color: '#aaa', fontSize: '.82rem', borderTop: '1px solid #f0ede8' }}>
              💡 Glissez les lignes pour changer l'ordre d'affichage sur la home page.
            </p>
          </div>
        )}

        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
            <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto', padding: '2rem', boxShadow: '0 24px 64px rgba(0,0,0,.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{editingSection ? '✏️ Modifier la section' : '➕ Nouvelle section'}</h2>
                <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Titre de la section *</label>
                  <input className="form-control" value={form.title} onChange={handleTitleChange} placeholder="ex: New Arrivals, Best Sellers…" autoFocus />
                </div>
                <div className="form-group">
                  <label>Slug (URL)</label>
                  <input className="form-control" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="auto-généré depuis le titre" style={{ fontFamily: 'monospace', fontSize: '.9rem' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Nb max de produits affichés</label>
                    <input type="number" min="1" max="20" className="form-control" value={form.product_limit} onChange={e => setForm(f => ({ ...f, product_limit: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Statut</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginTop: '.7rem' }}>
                      <input type="checkbox" id="is_active_cb" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                      <label htmlFor="is_active_cb" style={{ cursor: 'pointer', marginBottom: 0 }}>Section active</label>
                    </div>
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '.25rem', background: '#f7f6f3', borderRadius: 8, padding: '.75rem .9rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.6rem' }}>
                    <input type="checkbox" id="show_in_banner_cb" checked={form.show_in_banner} onChange={e => setForm(f => ({ ...f, show_in_banner: e.target.checked }))} style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 2 }} />
                    <label htmlFor="show_in_banner_cb" style={{ cursor: 'pointer', marginBottom: 0 }}>
                      <strong>Afficher dans le banner de navigation</strong><br />
                      <span style={{ color: '#888', fontSize: '.85rem' }}>Le titre apparaîtra comme lien en haut du site.</span>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Produits&nbsp;<span style={{ fontWeight: 400, color: '#888' }}>({form.product_ids.length} sélectionné{form.product_ids.length > 1 ? 's' : ''})</span></label>
                  <input className="form-control" placeholder="🔍 Rechercher un produit..." value={productSearch} onChange={e => setPSearch(e.target.value)} style={{ marginBottom: '.6rem' }} />
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 260, overflowY: 'auto', padding: '.4rem' }}>
                    {filteredProducts.length === 0
                      ? <p style={{ color: '#aaa', padding: '.5rem .75rem', margin: 0, fontSize: '.9rem' }}>Aucun produit trouvé.</p>
                      : filteredProducts.map(p => <ProductCheckbox key={p.id} product={p} selected={form.product_ids.includes(p.id)} onToggle={toggleProduct} />)
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" className="btn btn-outline" onClick={closeModal}>Annuler</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sauvegarde...' : editingSection ? '💾 Mettre à jour' : '✅ Créer la section'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminSections;